import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import { styled } from '@mui/material/styles';

import { colors, devices } from 'styles';

import FeedbackIcon from 'assets/customer-cases/feedback.svg';
import { FC } from 'react';
import { IResultData } from 'types/customer-case';

const FeedbackForm = styled(Box)`
  background-color: ${colors.lightGray};
  border-radius: 16px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 0;
  margin-top: 80px;
  max-width: 100%;

  @media (${devices.md}) {
    margin-left: 80px;
    max-width: 560px;
    margin-top: 0;
  }
`;

const FeedbackFormCompact = styled(Box)`
  background-color: ${colors.lightGray};
  border-radius: 16px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
`;

export const Results: FC<IResultData> = ({
  clientFeedbackMessage,
  clientLabel,
  secondClientFeedbackMessage,
  secondClientLabel,
  description,
  listItems,
  listTitle,
  resultDescription,
  resultTitle,
  title,
  rightColumnLayout,
}) => {
  const hasFeedback = clientFeedbackMessage && clientLabel;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const hasSecondFeedback = secondClientFeedbackMessage && secondClientLabel;

  if (rightColumnLayout) {
    return (
      <Box
        display="flex"
        width="100%"
        justifyContent="space-between"
        paddingTop="80px"
        paddingBottom="89px"
        alignItems={{ xxs: 'stretch', md: 'flex-start' }}
        flexDirection={{ xxs: 'column', md: 'row' }}
      >
        <Box
          maxWidth={{
            md: '560px',
            xxs: '100%',
          }}
        >
          <Typography
            color={colors.blue}
            fontWeight={700}
            fontSize={{
              md: '40px',
              xxs: '32px',
            }}
            lineHeight="120%"
          >
            {title}
          </Typography>

          <Typography
            color={colors.mainDark}
            fontSize="18px"
            lineHeight="150%"
            margin="24px 0 48px"
            whiteSpace="pre-line"
          >
            {description}
          </Typography>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap="24px"
          maxWidth={{ md: '560px', xxs: '100%' }}
          marginLeft={{ md: '80px', xxs: '0' }}
          marginTop={{ xxs: '48px', md: '200px' }}
        >
          <Box>
            <Typography fontWeight={700} fontSize="20px" lineHeight="140%" color={colors.mainDark}>
              {listTitle}
            </Typography>

            <ul
              style={{
                color: colors.mainDark,
                marginTop: '34px',
              }}
            >
              {listItems.map((item, idx) => (
                <li
                  key={idx}
                  style={{
                    margin: '8px 0',
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </Box>

          <Box>
            <Typography
              margin="24px 0 16px"
              fontWeight={700}
              fontSize="24px"
              lineHeight="140%"
              color={colors.mainDark}
            >
              {resultTitle}
            </Typography>

            <Typography lineHeight="140%" color={colors.mainDark} fontSize="18px">
              {resultDescription}
            </Typography>
          </Box>

          {hasFeedback && (
            <FeedbackFormCompact>
              <Box>
                <Box
                  sx={{
                    '& img': {
                      display: 'block',
                    },
                  }}
                >
                  <img src={FeedbackIcon} alt="feedback" />
                </Box>

                <Typography
                  margin="16px 0"
                  color={colors.mainDark}
                  fontWeight={700}
                  fontSize="20px"
                  lineHeight="140%"
                >
                  Client Feedback
                </Typography>

                <Typography fontSize="16px" lineHeight="150%" color={colors.mainDark}>
                  {clientFeedbackMessage}
                </Typography>

                <Typography
                  margin="16px 0"
                  fontSize="16px"
                  lineHeight="150%"
                  color={colors.mainDark}
                  fontWeight={300}
                  fontStyle="italic"
                  marginTop="32px"
                >
                  {' '}
                  {clientLabel}
                </Typography>
              </Box>
            </FeedbackFormCompact>
          )}

          {hasSecondFeedback && (
            <FeedbackFormCompact>
              <Box>
                <Box
                  sx={{
                    '& img': {
                      display: 'block',
                    },
                  }}
                >
                  <img src={FeedbackIcon} alt="feedback" />
                </Box>

                <Typography
                  margin="16px 0"
                  color={colors.mainDark}
                  fontWeight={700}
                  fontSize="20px"
                  lineHeight="140%"
                >
                  Client Feedback
                </Typography>

                <Typography fontSize="16px" lineHeight="150%" color={colors.mainDark}>
                  {secondClientFeedbackMessage}
                </Typography>

                <Typography
                  margin="16px 0"
                  fontSize="16px"
                  lineHeight="150%"
                  color={colors.mainDark}
                  fontWeight={300}
                  fontStyle="italic"
                  marginTop="32px"
                >
                  {' '}
                  {secondClientLabel}
                </Typography>
              </Box>
            </FeedbackFormCompact>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      width="100%"
      justifyContent="space-between"
      paddingTop="80px"
      paddingBottom="89px"
      alignItems="center"
      flexDirection={{ xxs: 'column', md: 'row' }}
    >
      <Box
        maxWidth={
          hasFeedback
            ? {
                md: '560px',
                xxs: '100%',
              }
            : {
                lg: '800px',
                xxs: '100%',
              }
        }
      >
        <Typography
          color={colors.blue}
          fontWeight={700}
          fontSize={{
            md: '40px',
            xxs: '32px',
          }}
          lineHeight="120%"
        >
          {title}
        </Typography>

        <Typography
          color={colors.mainDark}
          fontSize="18px"
          lineHeight="150%"
          margin="24px 0 48px"
          whiteSpace="pre-line"
        >
          {description}
        </Typography>

        <Typography fontWeight={700} fontSize="20px" lineHeight="140%" color={colors.mainDark}>
          {listTitle}
        </Typography>

        <ul
          style={{
            color: colors.mainDark,
            marginTop: '34px',
          }}
        >
          {listItems.map((item, idx) => (
            <li
              key={idx}
              style={{
                margin: '8px 0',
              }}
            >
              {item}
            </li>
          ))}
        </ul>

        <Typography
          margin="48px 0 24px"
          fontWeight={700}
          fontSize="24px"
          lineHeight="140%"
          color={colors.mainDark}
        >
          {resultTitle}
        </Typography>

        <Typography lineHeight="140%" color={colors.mainDark} fontSize="18px">
          {resultDescription}
        </Typography>
      </Box>

      {hasFeedback && (
        <FeedbackForm>
          <Box>
            <Box
              sx={{
                '& img': {
                  display: 'block',
                },
              }}
            >
              <img src={FeedbackIcon} alt="feedback" />
            </Box>

            <Typography
              margin="16px 0"
              color={colors.mainDark}
              fontWeight={700}
              fontSize="20px"
              lineHeight="140%"
            >
              Client Feedback
            </Typography>

            <Typography fontSize="16px" lineHeight="150%" color={colors.mainDark}>
              {clientFeedbackMessage}
            </Typography>

            <Typography
              margin="16px 0"
              fontSize="16px"
              lineHeight="150%"
              color={colors.mainDark}
              fontWeight={300}
              fontStyle="italic"
              marginTop="32px"
            >
              {' '}
              {clientLabel}
            </Typography>
          </Box>
        </FeedbackForm>
      )}
    </Box>
  );
};
